import React, { useEffect, useState } from "react";
import {
  FaHeartbeat,
  FaStethoscope,
  FaUserMd,
  FaPills,
  FaAmbulance,
  FaMobileAlt,
  FaShieldAlt,
  FaGlobeAfrica,
} from "react-icons/fa";
import styles from "./LoadingScreen.module.css";

interface LoadingScreenProps {
  onLoadingComplete?: () => void;
  duration?: number;
  showProgress?: boolean;
  customMessage?: string;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({
  onLoadingComplete,
  duration = 3000,
  showProgress = true,
  customMessage,
}) => {
  const [progress, setProgress] = useState(0);
  const [currentTip, setCurrentTip] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const healthTips = [
    "🩺 Regular checkups can prevent serious health issues",
    "💊 Always take medications as prescribed by your doctor",
    "🏃‍♂️ 30 minutes of daily exercise keeps you healthy",
    "💧 Drink at least 8 glasses of water daily",
    "😴 Quality sleep is essential for good health",
    "🥗 A balanced diet provides essential nutrients",
    "🧘‍♀️ Managing stress improves overall wellbeing",
  ];

  const floatingIcons = [
    { icon: FaHeartbeat, delay: 0, size: "2rem" },
    { icon: FaStethoscope, delay: 0.5, size: "1.8rem" },
    { icon: FaUserMd, delay: 1, size: "2.2rem" },
    { icon: FaPills, delay: 1.5, size: "1.6rem" },
    { icon: FaAmbulance, delay: 2, size: "2rem" },
    { icon: FaMobileAlt, delay: 2.5, size: "1.8rem" },
    { icon: FaShieldAlt, delay: 3, size: "1.9rem" },
    { icon: FaGlobeAfrica, delay: 3.5, size: "2.1rem" },
  ];

  useEffect(() => {
    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 100 / (duration / 50);
      });
    }, 50);

    // Health tips rotation
    const tipInterval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % healthTips.length);
    }, 1500);

    // Complete loading
    const loadingTimeout = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => {
        onLoadingComplete?.();
      }, 500);
    }, duration);

    return () => {
      clearInterval(progressInterval);
      clearInterval(tipInterval);
      clearTimeout(loadingTimeout);
    };
  }, [duration, onLoadingComplete, healthTips.length]);

  return (
    <div
      className={`${styles.loadingScreen} ${isExiting ? styles.exiting : ""}`}
    >
      <div className={styles.loadingGradient}>
        {/* Floating Medical Icons */}
        <div className={styles.floatingIcons}>
          {floatingIcons.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div
                key={index}
                className={styles.floatingIcon}
                style={{
                  animationDelay: `${item.delay}s`,
                  fontSize: item.size,
                  left: `${10 + index * 12}%`,
                  top: `${20 + (index % 3) * 25}%`,
                }}
              >
                <IconComponent />
              </div>
            );
          })}
        </div>

        <div className={styles.loadingContent}>
          {/* Company Logo/Brand */}
          <div className={styles.brandSection}>
            <div className={styles.logo}>
              <div className={styles.logoIcon}>
                <FaHeartbeat className={styles.logoHeart} />
              </div>
              <div className={styles.logoText}>
                <h1 className={styles.brandName}>LicoMeds</h1>
                <p className={styles.brandTagline}>
                  Healthcare for Everyone, Everywhere
                </p>
              </div>
            </div>
          </div>

          {/* Loading Animation */}
          <div className={styles.loadingAnimation}>
            <div className={styles.pulseContainer}>
              <div className={styles.pulseRing}></div>
              <div className={styles.pulseRing}></div>
              <div className={styles.pulseRing}></div>
              <div className={styles.centralIcon}>
                <FaUserMd />
              </div>
            </div>
          </div>

          {/* Loading Message */}
          <div className={styles.loadingMessage}>
            <h2 className={styles.mainMessage}>
              {customMessage || "Preparing Your Healthcare Experience"}
            </h2>
            <div className={styles.subMessage}>
              Connecting you to world-class medical professionals...
            </div>
          </div>

          {/* Progress Bar */}
          {showProgress && (
            <div className={styles.progressSection}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className={styles.progressText}>
                {Math.round(progress)}% Complete
              </div>
            </div>
          )}

          {/* Health Tips */}
          <div className={styles.healthTips}>
            <div className={styles.tipContainer}>
              <div className={styles.tipIcon}>💡</div>
              <div className={styles.tipContent}>
                <div className={styles.tipLabel}>Health Tip:</div>
                <div className={styles.tipText}>{healthTips[currentTip]}</div>
              </div>
            </div>
          </div>

          {/* Loading Stats */}
          <div className={styles.loadingStats}>
            <div className={styles.statItem}>
              <FaUserMd className={styles.statIcon} />
              <span>800+ Doctors</span>
            </div>
            <div className={styles.statItem}>
              <FaGlobeAfrica className={styles.statIcon} />
              <span>36 States</span>
            </div>
            <div className={styles.statItem}>
              <FaShieldAlt className={styles.statIcon} />
              <span>24/7 Support</span>
            </div>
          </div>

          {/* Network Status Indicator */}
          <div className={styles.networkStatus}>
            <div className={styles.networkDot}></div>
            <div className={styles.networkDot}></div>
            <div className={styles.networkDot}></div>
            <span className={styles.networkText}>Connecting...</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
